<template>
    <div>
        <app-layout>
            <template v-slot:header>
                <AppHeader
                    title="Döküman Sabit Ayarları"
                    :isFilter="false"
                />
            </template>
            <template v-slot:header-mobile>
                <HeaderMobile
                    title="Döküman Sabit Ayarları"
                    :isFilter="false"
                />
            </template>

            <section class="document-constant-page py-4">
                <div v-if="typesLoading" class="state-card">
                    <b-spinner small class="mr-2" />
                    Belge türleri yükleniyor...
                </div>

                <div v-else-if="!documentTypes.length" class="state-card">
                    <i class="ri-inbox-2-line mr-2"></i>
                    document_constant_types parametresinde belge türü bulunamadı.
                </div>

                <div v-else class="type-grid row mb-1">
                    <div
                        v-for="(documentType, index) in documentTypes"
                        :key="typeValue(documentType)"
                        class="col-6 document-type-col mb-3"
                    >
                        <button
                            type="button"
                            class="type-card w-100 h-100"
                            :class="{ active: selectedType === typeValue(documentType) }"
                            @click="selectType(documentType)"
                        >
                            <span class="type-card-icon">
                                <i :class="typeIcon(index)"></i>
                            </span>
                            <span class="type-card-content">
                                <strong>{{ typeLabel(documentType) }}</strong>
                                <small>{{ typeValue(documentType) }}</small>
                            </span>
                            <i class="ri-arrow-right-s-line type-arrow"></i>
                        </button>
                    </div>
                </div>

                <div v-if="selectedType" class="constant-section">
                    <div class="section-heading mb-3">
                        <div>
                            <span class="eyebrow">SEÇİLİ BELGE</span>
                            <div class="d-flex align-items-center">
                                <h4 class="mb-0">{{ selectedTypeLabel }}</h4>
                                <b-button
                                    v-if="canCreate"
                                    variant="outline-primary"
                                    size="sm"
                                    class="new-constant-button ml-3"
                                    @click="openCreateModal"
                                >
                                    <i class="ri-add-line mr-1"></i>
                                    Yeni
                                </b-button>
                            </div>
                        </div>
                        <span class="field-count">
                            {{ constants.length }} sabit alan
                        </span>
                    </div>

                    <div v-if="constantsLoading" class="state-card">
                        <b-spinner small class="mr-2" />
                        Sabit alanlar yükleniyor...
                    </div>

                    <div v-else-if="!constants.length" class="empty-state">
                        <span class="empty-icon"><i class="ri-file-add-line"></i></span>
                        <h5>Bu belge için henüz sabit alan yok</h5>
                        <p>İlk sabit alanı oluşturarak belge içeriğini yönetmeye başlayabilirsiniz.</p>
                        <b-button v-if="canCreate" variant="primary" @click="openCreateModal">
                            <i class="ri-add-line mr-1"></i>
                            Yeni sabit oluştur
                        </b-button>
                    </div>

                    <div v-else class="row">
                        <div
                            v-for="item in constants"
                            :key="item.id"
                            class="col-12 mb-3"
                            :class="{
                                'constant-drag-over': dragOverId === item.id,
                                'constant-dragging': draggedId === item.id
                            }"
                            @dragover.prevent="onDragOver(item)"
                            @drop.prevent="onDrop(item)"
                        >
                            <article class="constant-card h-100">
                                <div class="constant-card-header">
                                    <button
                                        v-if="canUpdate"
                                        type="button"
                                        class="drag-handle"
                                        draggable="true"
                                        title="Sıralamak için sürükleyin"
                                        aria-label="Sıralamak için sürükleyin"
                                        @dragstart.stop="startDrag($event, item)"
                                        @dragend="endDrag"
                                    >
                                        <i class="ri-drag-move-2-line"></i>
                                    </button>
                                    <b-button
                                        v-if="canUpdate"
                                        variant="light"
                                        class="edit-button"
                                        title="Tüm alanları düzenle"
                                        @click="openEditModal(item)"
                                    >
                                        <i class="ri-edit-line"></i>
                                    </b-button>
                                    <div class="min-width-0 flex-grow-1">
                                        <div class="d-flex align-items-center">
                                            <h5 class="mb-0 text-truncate">
                                                {{ item.name || item.field }}
                                            </h5>
                                            <button
                                                v-if="item.description"
                                                :id="`constant-info-${item.id}`"
                                                type="button"
                                                class="info-button ml-2"
                                                aria-label="Alan açıklamasını göster"
                                            >
                                                <i class="ri-information-line"></i>
                                            </button>
                                            <b-tooltip
                                                v-if="item.description"
                                                :target="`constant-info-${item.id}`"
                                                triggers="hover focus"
                                                placement="top"
                                            >
                                                {{ item.description }}
                                            </b-tooltip>
                                        </div>
                                        <small class="field-code">{{ item.field }}</small>
                                    </div>
                                    <b-button
                                        class="save-button"
                                        :variant="isDirty(item) ? 'primary' : 'light'"
                                        :disabled="!isDirty(item) || savingId === item.id"
                                        :title="isDirty(item) ? 'Değişiklikleri kaydet' : 'Değişiklik yok'"
                                        @click="saveItem(item)"
                                    >
                                        <b-spinner v-if="savingId === item.id" small />
                                        <i v-else class="ri-save-3-line"></i>
                                    </b-button>
                                </div>

                                <div class="language-fields">
                                    <b-form-group class="mb-0">
                                        <template #label>
                                            <span class="language-label">
                                                <span class="language-code tr">TR</span>
                                                Türkçe değer
                                            </span>
                                        </template>
                                        <b-form-textarea
                                            v-model="item.value"
                                            rows="2"
                                            max-rows="8"
                                            class="constant-textarea"
                                        />
                                    </b-form-group>
                                    <b-form-group class="mb-0">
                                        <template #label>
                                            <span class="language-label">
                                                <span class="language-code en">EN</span>
                                                İngilizce değer
                                            </span>
                                        </template>
                                        <b-form-textarea
                                            v-model="item.value_en"
                                            rows="2"
                                            max-rows="8"
                                            class="constant-textarea"
                                        />
                                    </b-form-group>
                                </div>
                            </article>
                        </div>
                    </div>
                </div>
            </section>

            <CommonModal
                ref="createModal"
                size="lg"
                :onHideOnlyX="true"
                @bHideModalHeaderClose="resetCreateForm"
            >
                <template v-slot:CommonModalTitle>
                    {{ modalMode === "edit" ? "DÖKÜMAN SABİTİNİ DÜZENLE" : "YENİ DÖKÜMAN SABİTİ" }}
                </template>
                <template v-slot:CommonModalContent>
                    <ValidationObserver ref="createFormObserver">
                        <div class="create-form">
                            <div class="form-section-title">
                                <i class="ri-file-add-line"></i>
                                Sabit alan bilgileri
                            </div>
                            <b-row>
                                <b-col md="4">
                                    <b-form-group label="Görünen Ad">
                                        <b-form-input v-model="createForm.name" maxlength="255" />
                                    </b-form-group>
                                </b-col>
                                <b-col md="4">
                                    <ValidationProvider name="Alan kodu" rules="required" v-slot="{ errors }">
                                        <b-form-group label="Alan Kodu">
                                            <b-form-input
                                                v-model="createForm.field"
                                                maxlength="255"
                                                placeholder="Örn. document_footer_text"
                                                :state="errors[0] ? false : null"
                                            />
                                            <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                                        </b-form-group>
                                    </ValidationProvider>
                                </b-col>
                                <b-col md="4">
                                    <ValidationProvider name="Sıralama" rules="required|numeric|min_value:1" v-slot="{ errors }">
                                        <b-form-group label="Sıralama">
                                            <b-form-input
                                                v-model.number="createForm.rank"
                                                type="number"
                                                min="1"
                                                :state="errors[0] ? false : null"
                                            />
                                            <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                                        </b-form-group>
                                    </ValidationProvider>
                                </b-col>
                                <b-col md="6">
                                    <ValidationProvider name="Türkçe değer" rules="required" v-slot="{ errors }">
                                        <b-form-group label="Türkçe Değer">
                                            <b-form-textarea
                                                v-model="createForm.value"
                                                rows="4"
                                                maxlength="255"
                                                :state="errors[0] ? false : null"
                                            />
                                            <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                                        </b-form-group>
                                    </ValidationProvider>
                                </b-col>
                                <b-col md="6">
                                    <ValidationProvider name="İngilizce değer" rules="required" v-slot="{ errors }">
                                        <b-form-group label="İngilizce Değer">
                                            <b-form-textarea
                                                v-model="createForm.value_en"
                                                rows="4"
                                                maxlength="255"
                                                :state="errors[0] ? false : null"
                                            />
                                            <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                                        </b-form-group>
                                    </ValidationProvider>
                                </b-col>
                                <b-col md="12">
                                    <b-form-group label="Açıklama">
                                        <b-form-textarea
                                            v-model="createForm.description"
                                            rows="3"
                                            placeholder="Bu alanın belgede nerede ve nasıl kullanıldığını açıklayın."
                                        />
                                    </b-form-group>
                                </b-col>
                            </b-row>
                            <div class="d-flex justify-content-between align-items-center">
                                <b-button
                                    v-if="modalMode === 'edit' && canDelete"
                                    variant="outline-danger"
                                    :disabled="deleting || creating"
                                    @click="deleteItem"
                                >
                                    <b-spinner v-if="deleting" small class="mr-1" />
                                    <i v-else class="ri-delete-bin-7-line mr-1"></i>
                                    Sil
                                </b-button>
                                <span v-else></span>
                                <b-button
                                    variant="primary"
                                    class="px-4"
                                    :disabled="creating || deleting"
                                    @click="submitForm"
                                >
                                    <b-spinner v-if="creating" small class="mr-1" />
                                    <i v-else class="ri-save-3-line mr-1"></i>
                                    {{ modalMode === "edit" ? "Güncelle" : "Kaydet" }}
                                </b-button>
                            </div>
                        </div>
                    </ValidationObserver>
                </template>
            </CommonModal>
        </app-layout>
    </div>
</template>

<script>
import AppLayout from "@/layouts/AppLayout";
import AppHeader from "@/layouts/AppLayout/Header";
import HeaderMobile from "@/layouts/AppLayout/HeaderMobile";
import CommonModal from "@/components/elements/CommonModal";
import ParameterService from "@/services/ParameterService";
import DocumentConstantService from "@/services/DocumentConstantService";

const emptyCreateForm = () => ({
    type: null,
    name: null,
    field: null,
    rank: 1,
    value: null,
    value_en: null,
    description: null
});

export default {
    name: "DocumentConstantSettings",
    components: {
        AppLayout,
        AppHeader,
        HeaderMobile,
        CommonModal
    },
    metaInfo() {
        return {
            title: "Döküman Sabit Ayarları"
        };
    },
    data() {
        return {
            documentTypes: [],
            selectedType: null,
            selectedTypeLabel: null,
            constants: [],
            originalValues: {},
            typesLoading: false,
            constantsLoading: false,
            savingId: null,
            creating: false,
            deleting: false,
            ranking: false,
            draggedId: null,
            dragOverId: null,
            dragStartConstants: null,
            modalMode: "create",
            editingId: null,
            createForm: emptyCreateForm()
        };
    },
    computed: {
        canCreate() {
            return this.selectedType && this.checkPermission("constant_store");
        },
        canUpdate() {
            return this.checkPermission("constant_update");
        },
        canDelete() {
            return this.checkPermission("constant_delete");
        }
    },
    created() {
        this.loadDocumentTypes();
    },
    methods: {
        typeValue(item) {
            return String(item.code || item.data || item.value || item.id);
        },
        typeLabel(item) {
            return item.name || item.name_en || item.label || this.typeValue(item);
        },
        typeIcon(index) {
            const icons = [
                "ri-file-text-line",
                "ri-graduation-cap-line",
                "ri-award-line",
                "ri-article-line",
                "ri-file-user-line",
                "ri-file-list-3-line"
            ];
            return icons[index % icons.length];
        },
        async loadDocumentTypes() {
            this.typesLoading = true;
            try {
                const response = await ParameterService.getItems("document_constant_types", {
                    params: {
                        sort: "rank"
                    }
                });
                const parameter = response.data.data || {};
                this.documentTypes = Array.isArray(parameter.items)
                    ? parameter.items.slice().sort((first, second) => {
                        return Number(first.rank || 0) - Number(second.rank || 0);
                    })
                    : [];

                if (this.documentTypes.length) {
                    await this.selectType(this.documentTypes[0]);
                }
            } finally {
                this.typesLoading = false;
            }
        },
        async selectType(documentType) {
            const type = this.typeValue(documentType);
            if (this.selectedType === type && this.constants.length) {
                return;
            }

            this.selectedType = type;
            this.selectedTypeLabel = this.typeLabel(documentType);
            await this.loadConstants();
        },
        async loadConstants() {
            this.constantsLoading = true;
            try {
                const response = await DocumentConstantService.getByType(this.selectedType);
                this.constants = (response.data.data || [])
                    .map(item => ({ ...item }))
                    .sort((first, second) => Number(first.rank) - Number(second.rank));
                this.originalValues = this.constants.reduce((values, item) => {
                    values[item.id] = {
                        value: item.value,
                        value_en: item.value_en
                    };
                    return values;
                }, {});
            } finally {
                this.constantsLoading = false;
            }
        },
        isDirty(item) {
            const original = this.originalValues[item.id];
            return original && (
                original.value !== item.value ||
                original.value_en !== item.value_en
            );
        },
        async saveItem(item) {
            if (!this.isDirty(item)) {
                return;
            }

            this.savingId = item.id;
            try {
                await DocumentConstantService.update(item.id, {
                    type: item.type,
                    name: item.name,
                    field: item.field,
                    rank: item.rank,
                    value: item.value,
                    value_en: item.value_en,
                    description: item.description
                });
                this.originalValues[item.id] = {
                    value: item.value,
                    value_en: item.value_en
                };
                this.$toast.success("Döküman sabiti güncellendi.");
            } finally {
                this.savingId = null;
            }
        },
        openCreateModal() {
            this.resetCreateForm();
            this.modalMode = "create";
            this.createForm.type = this.selectedType;
            this.createForm.rank = this.constants.length
                ? Math.max(...this.constants.map(item => Number(item.rank) || 0)) + 1
                : 1;
            this.$refs.createModal.$refs.commonModal.show();
        },
        openEditModal(item) {
            this.resetCreateForm();
            this.modalMode = "edit";
            this.editingId = item.id;
            this.createForm = {
                type: item.type,
                name: item.name,
                field: item.field,
                rank: item.rank,
                value: item.value,
                value_en: item.value_en,
                description: item.description
            };
            this.$refs.createModal.$refs.commonModal.show();
        },
        resetCreateForm() {
            this.createForm = emptyCreateForm();
            this.modalMode = "create";
            this.editingId = null;
            if (this.$refs.createFormObserver) {
                this.$refs.createFormObserver.reset();
            }
        },
        async submitForm() {
            const valid = await this.$refs.createFormObserver.validate();
            if (!valid) {
                return;
            }

            this.creating = true;
            try {
                if (this.modalMode === "edit") {
                    await DocumentConstantService.update(this.editingId, this.createForm);
                    this.$toast.success("Döküman sabiti güncellendi.");
                } else {
                    await DocumentConstantService.store(this.createForm);
                    this.$toast.success("Yeni döküman sabiti oluşturuldu.");
                }
                this.$refs.createModal.$refs.commonModal.hide();
                await this.loadConstants();
                this.resetCreateForm();
            } finally {
                this.creating = false;
            }
        },
        startDrag(event, item) {
            if (this.ranking) {
                event.preventDefault();
                return;
            }

            this.draggedId = item.id;
            this.dragStartConstants = this.constants.map(constant => ({ ...constant }));
            event.dataTransfer.effectAllowed = "move";
            event.dataTransfer.setData("text/plain", String(item.id));
        },
        onDragOver(item) {
            if (!this.draggedId || this.draggedId === item.id) {
                return;
            }

            const sourceIndex = this.constants.findIndex(constant => constant.id === this.draggedId);
            const targetIndex = this.constants.findIndex(constant => constant.id === item.id);

            if (sourceIndex < 0 || targetIndex < 0) {
                return;
            }

            const reorderedConstants = this.constants.slice();
            const [movedItem] = reorderedConstants.splice(sourceIndex, 1);
            reorderedConstants.splice(targetIndex, 0, movedItem);
            this.constants = reorderedConstants.map((constant, index) => ({
                ...constant,
                rank: index + 1
            }));
            this.dragOverId = item.id;
        },
        async onDrop() {
            if (!this.draggedId || !this.dragStartConstants) {
                this.endDrag(false);
                return;
            }

            const previousConstants = this.dragStartConstants;
            const previousIds = previousConstants.map(item => item.id);
            const currentIds = this.constants.map(item => item.id);
            const orderChanged = previousIds.some((id, index) => id !== currentIds[index]);
            this.endDrag(false);

            if (!orderChanged) {
                return;
            }

            this.ranking = true;

            try {
                await DocumentConstantService.updateRanks(
                    this.selectedType,
                    this.constants.map(item => item.id)
                );
                this.$toast.success("Sabit alan sıralaması güncellendi.");
            } catch {
                this.constants = previousConstants;
            } finally {
                this.ranking = false;
            }
        },
        endDrag(restore = true) {
            if (restore && this.dragStartConstants) {
                this.constants = this.dragStartConstants;
            }
            this.draggedId = null;
            this.dragOverId = null;
            this.dragStartConstants = null;
        },
        deleteItem() {
            this.$swal.fire({
                text: "Bu döküman sabitini silmek istediğinize emin misiniz?",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Evet, sil",
                cancelButtonText: "Hayır"
            }).then(async result => {
                if (!result.isConfirmed) {
                    return;
                }

                this.deleting = true;
                try {
                    await DocumentConstantService.remove(this.editingId);
                    this.$toast.success("Döküman sabiti silindi.");
                    this.$refs.createModal.$refs.commonModal.hide();
                    this.resetCreateForm();
                    await this.loadConstants();
                } finally {
                    this.deleting = false;
                }
            });
        }
    }
};
</script>

<style lang="scss" scoped>
.document-constant-page {
    --ink: #26334d;
    --muted: #74809a;
    --primary-soft: #eef3ff;
    --border: #e5e9f2;
}

.eyebrow {
    color: #6079ad;
    display: block;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1.2px;
    margin-bottom: 5px;
}

.type-grid {
    margin-left: -6px;
    margin-right: -6px;

    > div {
        padding-left: 6px;
        padding-right: 6px;
    }
}

@media (min-width: 768px) {
    .document-type-col {
        flex: 0 0 20%;
        max-width: 20%;
    }
}

.type-card {
    align-items: center;
    background: #fff;
    border: 1px solid var(--border);
    border-radius: 14px;
    color: var(--ink);
    display: flex;
    min-height: 82px;
    padding: 14px 16px;
    text-align: left;
    transition: border-color .18s ease, box-shadow .18s ease, transform .18s ease;

    &:hover {
        border-color: #b9c8e7;
        box-shadow: 0 8px 20px rgba(50, 67, 100, .08);
        transform: translateY(-2px);
    }

    &.active {
        background: var(--primary-soft);
        border-color: #839bd0;
        box-shadow: inset 0 0 0 1px #839bd0;
    }
}

.type-card-icon {
    align-items: center;
    background: #f0f3f9;
    border-radius: 11px;
    color: #647cac;
    display: flex;
    flex: 0 0 44px;
    font-size: 21px;
    height: 44px;
    justify-content: center;
    margin-right: 12px;
}

.type-card.active .type-card-icon {
    background: #fff;
    color: #4767a6;
}

.type-card-content {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;

    strong {
        font-size: 14px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    small {
        color: var(--muted);
        margin-top: 3px;
    }
}

.type-arrow {
    color: #9aa5ba;
    font-size: 20px;
}

.section-heading {
    align-items: center;
    display: flex;
    justify-content: space-between;

    h4 {
        color: var(--ink);
        font-weight: 700;
    }
}

.field-count {
    background: #f1f3f7;
    border-radius: 20px;
    color: #68748a;
    font-size: 12px;
    padding: 7px 12px;
}

.constant-card {
    background: #fff;
    border: 1px solid var(--border);
    border-radius: 16px;
    box-shadow: 0 4px 14px rgba(39, 55, 85, .045);
    display: grid;
    grid-template-columns: minmax(260px, 32%) minmax(0, 1fr);
    overflow: hidden;
    transition: border-color .18s ease, box-shadow .18s ease, transform .18s ease;

    &:hover {
        border-color: #c7d2e5;
        box-shadow: 0 8px 22px rgba(39, 55, 85, .08);
        transform: translateY(-1px);
    }
}

.constant-dragging {
    opacity: .45;
}

.constant-drag-over .constant-card {
    border-color: #6985bd;
    box-shadow: 0 0 0 3px rgba(84, 112, 170, .14);
}

.constant-card-header {
    align-items: center;
    background: linear-gradient(135deg, #fbfcff 0%, #f6f8fc 100%);
    border-right: 1px solid #edf0f5;
    display: flex;
    justify-content: space-between;
    padding: 18px 20px;

    h5 {
        color: var(--ink);
        font-size: 15px;
        font-weight: 700;
    }
}

.drag-handle {
    align-items: center;
    background: transparent;
    border: 0;
    color: #9aa5ba;
    cursor: grab;
    display: flex;
    flex: 0 0 28px;
    font-size: 19px;
    height: 36px;
    justify-content: center;
    margin-left: -7px;
    margin-right: 5px;
    padding: 0;

    &:hover {
        color: #4f6fa9;
    }

    &:active {
        cursor: grabbing;
    }
}

.min-width-0 {
    min-width: 0;
}

.field-code {
    color: #8a94a8;
    display: block;
    font-family: monospace;
    margin-top: 4px;
}

.info-button {
    align-items: center;
    background: #eef3ff;
    border: 0;
    border-radius: 50%;
    color: #5873ac;
    display: inline-flex;
    flex: 0 0 25px;
    height: 25px;
    justify-content: center;
}

.save-button {
    align-items: center;
    border-radius: 10px;
    display: flex;
    flex: 0 0 40px;
    font-size: 18px;
    height: 40px;
    justify-content: center;
    margin-left: 12px;
    padding: 0;
}

.edit-button {
    align-items: center;
    border: 1px solid #e1e6ef;
    border-radius: 9px;
    color: #66758f;
    display: flex;
    flex: 0 0 36px;
    font-size: 16px;
    height: 36px;
    justify-content: center;
    margin-right: 11px;
    padding: 0;

    &:hover {
        background: #eef3ff;
        border-color: #cbd7ed;
        color: #4f6fa9;
    }
}

.language-fields {
    align-items: center;
    display: grid;
    gap: 20px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    padding: 15px 20px;
}

.language-label {
    align-items: center;
    color: #556078;
    display: inline-flex;
    font-size: 12px;
    font-weight: 600;
}

.language-code {
    border-radius: 5px;
    color: #fff;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: .4px;
    margin-right: 7px;
    padding: 3px 5px;

    &.tr {
        background: #d85b68;
    }

    &.en {
        background: #5375ac;
    }
}

.constant-textarea {
    background: #fbfcfe;
    border-color: #dfe4ed;
    border-radius: 10px;
    font-size: 13px;
    resize: vertical;

    &:focus {
        background: #fff;
        border-color: #839bd0;
        box-shadow: 0 0 0 3px rgba(99, 126, 185, .12);
    }
}

.state-card,
.empty-state {
    background: #fff;
    border: 1px dashed #ccd4e2;
    border-radius: 15px;
    color: var(--muted);
    padding: 28px;
    text-align: center;
}

.empty-state {
    padding: 46px 20px;

    h5 {
        color: var(--ink);
        font-weight: 700;
        margin-top: 14px;
    }

    p {
        color: var(--muted);
        margin: 6px auto 18px;
        max-width: 520px;
    }
}

.empty-icon {
    align-items: center;
    background: #eef3ff;
    border-radius: 16px;
    color: #607cb4;
    display: inline-flex;
    font-size: 28px;
    height: 58px;
    justify-content: center;
    width: 58px;
}

.create-form {
    padding: 4px 2px;
}

.form-section-title {
    align-items: center;
    border-bottom: 1px solid #e8ebf1;
    color: var(--ink);
    display: flex;
    font-size: 14px;
    font-weight: 700;
    margin-bottom: 20px;
    padding-bottom: 12px;

    i {
        color: #607cb4;
        font-size: 20px;
        margin-right: 8px;
    }
}

@media (max-width: 767px) {
    .constant-card {
        display: block;
    }

    .constant-card-header {
        border-bottom: 1px solid #edf0f5;
        border-right: 0;
    }

    .language-fields {
        grid-template-columns: 1fr;
    }

    .section-heading {
        align-items: flex-start;
        flex-direction: column;
    }

    .field-count {
        margin-top: 10px;
    }
}
</style>
